import { X, Crown, Coins, Sparkles, Share2 } from 'lucide-react';
import { toast } from 'sonner';

import { cn, formatLamports } from '@/lib/utils';
import {
  consolidateRewards,
  formatConsolidatedReward,
  getConsolidatedRewardEmoji,
  getRewardSummary,
} from '@/lib/rewardConsolidation';
import type { Reward } from '@/types/schemas';
import { useShare } from '@/hooks/useShare';

interface RewardModalProps {
  open: boolean;
  onClose: () => void;
  rewards: Reward[];
  totalPoints?: number; // Optional: Total points earned (for accurate share text)
}

export function RewardModal({ open, onClose, rewards, totalPoints }: RewardModalProps) {
  const { shareChest, canShare, getPotentialPoints, isSharing } = useShare();

  if (!open || rewards.length === 0) {
    return null;
  }

  // Consolidate similar rewards
  const consolidatedRewards = consolidateRewards(rewards);
  const summary = getRewardSummary(consolidatedRewards);
  const hasJackpot = rewards.some((r) => r.type === 'jackpot' && r.is_jackpot_win);
  const totalValue = rewards.reduce((sum, reward) => sum + Number(reward.amount_lamports), 0);
  const totalSol = totalValue / 1_000_000_000; // Convert lamports to SOL

  const canShareToday = canShare('chest');
  const sharePoints = getPotentialPoints('chest');

  const handleShare = async () => {
    try {
      // Use provided totalPoints if available, otherwise estimate from SOL (not ideal but fallback)
      const pointsToShare = totalPoints ?? Math.round(totalSol * 1000);
      const result = await shareChest(pointsToShare);
      if (result.pointsEarned > 0) {
        toast.success(`+${result.pointsEarned} Points!`, {
          description: 'Share tracked successfully',
        });
      } else {
        toast.info('Share opened', {
          description: result.message,
        });
      }
    } catch (error) {
      toast.error('Failed to track share', {
        description: error instanceof Error ? error.message : 'Unknown error',
      });
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div
        className={cn(
          'relative w-full max-w-md',
          'bg-card border border-border rounded-xl shadow-2xl',
          'animate-bounce-in',
          hasJackpot && 'ring-4 ring-yellow-400/50'
        )}
      >
        {/* Header */}
        <div
          className={cn(
            'relative p-6 text-center',
            hasJackpot && 'bg-gradient-to-br from-yellow-400/20 to-primary/20'
          )}
        >
          <button
            onClick={onClose}
            className={cn(
              'absolute top-4 right-4 p-1 rounded-full',
              'text-muted-foreground hover:text-foreground',
              'hover:bg-muted transition-colors'
            )}
          >
            <X size={20} />
          </button>

          <div className="mb-4">
            {hasJackpot ? (
              <Crown size={48} className="mx-auto text-yellow-400" />
            ) : (
              <Coins size={48} className="mx-auto text-primary" />
            )}
          </div>

          <h2 className={cn('text-2xl font-bold mb-2', hasJackpot && 'text-yellow-400')} style={{ fontFamily: 'Outfit, sans-serif' }}>
            {hasJackpot ? '🎉 Jackpot Winner!' : '🎁 Meme Reward!'}
          </h2>

          <p className="text-muted-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>
            You won {summary.totalRewards} prize{summary.totalRewards > 1 ? 's' : ''}
            {summary.uniqueTypes > 1 ? ` of ${summary.uniqueTypes} different types` : ''}!
          </p>
          {summary.totalValue > 0 && (
            <p className="text-xs text-muted-foreground mt-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Total value: <span style={{ fontFamily: 'Outfit, sans-serif' }}>$</span>{summary.totalValue.toFixed(2)}
            </p>
          )}
        </div>

        {/* Consolidated Rewards List */}
        <div className="p-6 space-y-4">
          {consolidatedRewards.map((reward, index) => {
            const { displayName, displayValue, displayCount } = formatConsolidatedReward(reward);
            const emoji = getConsolidatedRewardEmoji(reward);

            return (
              <div
                key={index}
                className={cn(
                  'p-4 rounded-lg border',
                  reward.is_jackpot_win
                    ? 'border-yellow-400/30 bg-yellow-400/10'
                    : 'border-primary/30 bg-primary/10'
                )}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <span className="text-2xl">{emoji}</span>
                    <div>
                      <div className="font-medium flex items-center space-x-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        <span>{displayName}</span>
                        {reward.count > 1 && (
                          <span className="text-xs bg-primary/20 text-primary px-2 py-1 rounded-full">
                            {displayCount}
                          </span>
                        )}
                      </div>
                      {reward.is_jackpot_win && (
                        <div className="text-xs text-yellow-600" style={{ fontFamily: 'Outfit, sans-serif' }}>🏆 JACKPOT WIN!</div>
                      )}
                      {reward.tier !== undefined && !reward.is_jackpot_win && (
                        <div className="text-sm text-muted-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>Tier {reward.tier}</div>
                      )}
                    </div>
                  </div>

                  <div className="text-right">
                    <div
                      className={cn(
                        'text-lg font-bold',
                        reward.is_jackpot_win ? 'text-yellow-400' : 'text-primary'
                      )}
                      style={{ fontFamily: 'Outfit, sans-serif' }}
                    >
                      {displayValue}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}

          {/* Total */}
          {consolidatedRewards.length > 1 && (
            <div className="border-t pt-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Sparkles size={20} className="text-primary" />
                  <span className="font-medium" style={{ fontFamily: 'Outfit, sans-serif' }}>Total Won</span>
                </div>
                <div
                  className={cn(
                    'text-xl font-bold',
                    hasJackpot ? 'text-yellow-400' : 'text-primary'
                  )}
                  style={{ fontFamily: 'Outfit, sans-serif' }}
                >
                  {summary.totalValue > 0
                    ? `$${summary.totalValue.toFixed(2)}`
                    : `${formatLamports(totalValue.toString())} SOL`}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t space-y-3">
          <button
            onClick={handleShare}
            disabled={isSharing}
            className={cn(
              'w-full p-3 rounded-lg font-medium',
              'flex items-center justify-center space-x-2',
              canShareToday
                ? 'bg-blue-500 text-white hover:bg-blue-600'
                : 'bg-muted text-muted-foreground hover:bg-muted/80',
              'transition-colors disabled:opacity-50'
            )}
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            <Share2 size={18} />
            <span>
              {sharePoints > 0
                ? `Share to Earn ${sharePoints} Points`
                : 'Share on X'}
            </span>
          </button>
          <button
            onClick={onClose}
            className={cn(
              'w-full p-3 rounded-lg font-medium',
              'bg-primary text-primary-foreground',
              'hover:bg-primary/90 transition-colors'
            )}
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            Close
          </button>
        </div>

        {/* Celebration Effects */}
        {hasJackpot && (
          <div className="absolute inset-0 pointer-events-none">
            {[...Array(12)].map((_, i) => (
              <div
                key={i}
                className={cn(
                  'absolute w-2 h-2 rounded-full',
                  'bg-gradient-to-r from-yellow-400 to-orange-400',
                  'animate-bounce'
                )}
                style={{
                  top: `${10 + (i % 4) * 20}%`,
                  left: `${5 + (i % 6) * 15}%`,
                  animationDelay: `${i * 0.1}s`,
                  animationDuration: `${1 + (i % 3) * 0.5}s`,
                }}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
